import './firebase.ts';
import { getDatabase, ref, get, push, set, onValue } from 'firebase/database';
import { auth } from './firebase.ts';
import { auditLog, logError } from './auditLog.ts';
import { translateApiError } from '../utils/errorTranslations.ts';
import { enrichBookingsWithGroups } from './bookingGroupUtils.ts';

// Types
export interface Station {
  id: string;
  imageLink: string;
  typeId: string;
  pricePerMonth?: number;
  pricePerDay?: number;
  pricePerBaseHour?: number; // Renamed from pricePerHour
  tickchakLink?: string;
}

export interface StationType {
  id: string;
  type: string;
  typeFriendlyName: string;
  imageLink?: string; // Image for this station type
  pricePerBaseHour?: number; // Renamed from pricePerHour
  pricePerDay?: number;
  pricePerMonth?: number;
  // Special tiered pricing fields
  baseHours?: number; // X hours - defines the base booking duration and slot size
  extraHourPrice?: number; // Z amount - price per additional hour beyond baseHours
  payLinks: {
    hour?: string;
    day?: string;
    month?: string;
  };
}

export interface Booking {
  id: string;
  stationId: string;
  userEmail: string;
  userName?: string; // Optional user name
  phoneNumber?: string; // Phone number
  approved?: boolean; // Resolved from BookingGroup on read; not stored on new Booking nodes
  fromDate: string; // ISO string
  toDate: string;   // ISO string
  keypadCode?: string; // Resolved from BookingGroup on read
  groupId?: string; // BookingGroup this occurrence belongs to
}

export interface BookingGroup {
  bookingIds: Record<string, boolean>;
  userEmail: string;
  userName?: string;
  phoneNumber?: string;
  approved: boolean;
  keypadCode?: string | null;
  createdAt: string;
}

export interface Config {
  booking: {
    startHour: string; // Format: "HH:MM" (e.g., "08:30")
    endHour: string;   // Format: "HH:MM" (e.g., "20:30")
  };
  Admins?: string; // Comma-separated admin emails
}

export interface Coupon {
  code: string;
  discount: number;
  type: 'percentage' | 'fixed';
  friendlyName: string;
  expirationDate: string; // Format: "YYYY-MM-DD"
  needProof?: boolean;
}

// Helper: Check if two date ranges overlap
function isOverlap(aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) {
  return aStart <= bEnd && bStart <= aEnd;
}

// Helper: Get authentication token for the current user
async function getAuthToken(): Promise<string> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('User not authenticated');
  }
  const token = await currentUser.getIdToken(true);
  return token;
}

// Firebase Functions configuration
const FIREBASE_PROJECT_ID = 'homat-shmuel-hub';
const FIREBASE_FUNCTIONS_REGION = 'europe-west1';
const FIREBASE_FUNCTIONS_EMULATOR_PORT = 5001;

// Helper: Build Firebase Cloud Function URL
function getFunctionUrl(functionName: string): string {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isLocal) {
    return `http://localhost:${FIREBASE_FUNCTIONS_EMULATOR_PORT}/${FIREBASE_PROJECT_ID}/${FIREBASE_FUNCTIONS_REGION}/${functionName}`;
  }
  
  return `https://${FIREBASE_FUNCTIONS_REGION}-${FIREBASE_PROJECT_ID}.cloudfunctions.net/${functionName}`;
}

// Get all stations
export async function getAllStations(): Promise<Station[]> {
  try {
    const db = getDatabase();
    const stationsSnap = await get(ref(db, 'Station'));
    const stations = stationsSnap.val();
    if (!stations) {
      await auditLog.stationsRead(0);
      return [];
    }
    const stationArray = Object.entries(stations).map(([id, data]: [string, any]) => ({ id, ...data }));
    await auditLog.stationsRead(stationArray.length);
    return stationArray;
  } catch (error) {
    console.error('Error fetching stations:', error);
    await auditLog.stationsRead(0);
    return [];
  }
}

// Get all station types
export async function getAllStationTypes(): Promise<StationType[]> {
  try {
    const db = getDatabase();
    const typesSnap = await get(ref(db, 'StationType'));
    const types = typesSnap.val();
    if (!types) {
      await auditLog.stationTypesRead(0);
      return [];
    }
    const typesArray = Object.entries(types).map(([id, data]: [string, any]) => ({ id, ...data }));
    await auditLog.stationTypesRead(typesArray.length);
    return typesArray;
  } catch (error) {
    console.error('Error fetching station types:', error);
    await auditLog.stationTypesRead(0);
    return [];
  }
}

// Get config data
export async function getConfig(): Promise<Config | null> {
  try {
    const db = getDatabase();
    const configSnap = await get(ref(db, 'Config'));
    const config = configSnap.val();
    if (!config) {
      console.warn('No Config found in database');
      return null;
    }
    return config as Config;
  } catch (error) {
    console.error('Error fetching config:', error);
    return null;
  }
}

// Get all coupons
export async function getAllCoupons(): Promise<Coupon[]> {
  try {
    const db = getDatabase();
    const couponsSnap = await get(ref(db, 'Cupons'));
    const coupons = couponsSnap.val();
    if (!coupons) {
      return [];
    }
    const couponsArray = Object.entries(coupons).map(([id, data]: [string, any]) => ({ id, ...data } as Coupon));
    return couponsArray;
  } catch (error) {
    console.error('Error fetching coupons:', error);
    return [];
  }
}

// Create or update a coupon
export async function saveCoupon(couponCode: string, coupon: Coupon): Promise<void> {
  try {
    const db = getDatabase();
    const couponRef = ref(db, `Cupons/${couponCode}`);
    await set(couponRef, coupon);
    await auditLog.recordCreated('Cupons', couponCode, coupon);
  } catch (error: any) {
    console.error('Error saving coupon:', error);
    await auditLog.recordCreationFailed('Cupons', error.message || 'Unknown error', coupon);
    throw error;
  }
}

// Check if user is admin based on config
export async function deleteCoupon(couponCode: string): Promise<void> {
  try {
    const db = getDatabase();
    const couponRef = ref(db, `Cupons/${couponCode}`);
    await set(couponRef, null);
    await auditLog.recordDeleted('Cupons', couponCode, {});
  } catch (error: any) {
    console.error('Error deleting coupon:', error);
    await logError('Error deleting coupon', {
      collection: 'Cupons',
      couponCode,
      errorMessage: error.message || 'Unknown error'
    });
    throw error;
  }
}

export async function isUserAdmin(userEmail: string | null | undefined): Promise<boolean> {
  if (!userEmail) {
    return false;
  }
  
  try {
    const config = await getConfig();
    if (!config?.Admins) {
      return false;
    }
    
    // Split comma-separated admin emails and check if user email is in the list
    const adminEmails = config.Admins.split(',').map(email => email.trim().toLowerCase());
    return adminEmails.includes(userEmail.toLowerCase());
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

// Helper function to create date-time strings with config hours for day/month bookings
export async function createBookingDates(slotType: string, selectedDate: string, selectedEndDate: string, selectedStartTime?: string, selectedEndTime?: string): Promise<{ fromDate: string; toDate: string }> {
  if (slotType === 'hour') {
    // For hourly bookings, use the selected times
    return {
      fromDate: `${selectedDate}T${selectedStartTime}`,
      toDate: `${selectedDate}T${selectedEndTime}`
    };
  }
  
  if (slotType === 'day' || slotType === 'month') {
    // For day/month bookings, use config hours
    const config = await getConfig();
    // Parse start and end hours from config (format: "HH:MM")
    const startHourStr = config?.booking?.startHour ?? '08:00';
    const endHourStr = config?.booking?.endHour ?? '19:00';
    
    return {
      fromDate: `${selectedDate}T${startHourStr}`,
      toDate: `${selectedEndDate}T${endHourStr}`
    };
  }
  
  // Fallback
  return {
    fromDate: selectedDate,
    toDate: selectedEndDate || selectedDate
  };
}

// Get all bookings
export async function getAllBookings(): Promise<Booking[]> {
  try {
    const db = getDatabase();
    const bookingsSnap = await get(ref(db, 'Booking'));
    const bookings = bookingsSnap.val() || {};
    // Firebase key must win over any nested `id` field (legacy numeric ids)
    const bookingsArray = Object.entries(bookings).map(([firebaseKey, data]: [string, any]) => ({
      ...data,
      id: firebaseKey,
    }));
    await auditLog.bookingsRead(bookingsArray.length);
    return bookingsArray;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    await auditLog.bookingsRead(0);
    return [];
  }
}

export async function getAllBookingGroups(): Promise<(BookingGroup & { id: string })[]> {
  try {
    const db = getDatabase();
    const snap = await get(ref(db, 'BookingGroup'));
    const groups = snap.val() || {};
    return Object.entries(groups).map(([id, data]: [string, any]) => ({ id, ...data }));
  } catch (error) {
    console.error('Error fetching booking groups:', error);
    return [];
  }
}

export async function approveBookingGroup(groupId: string, keypadCode?: string): Promise<void> {
  try {
    const db = getDatabase();
    const groupRef = ref(db, `BookingGroup/${groupId}`);
    const snap = await get(groupRef);
    const groupData = snap.val();
    if (!groupData) {
      throw new Error(`BookingGroup not found: ${groupId}`);
    }

    await set(groupRef, {
      ...groupData,
      approved: true,
      keypadCode: keypadCode ?? null,
    });
    await auditLog.recordUpdated('BookingGroup', groupId, {
      approved: true,
      ...(keypadCode && { keypadCode }),
    });
  } catch (error: any) {
    console.error('Error approving BookingGroup:', error);
    await auditLog.recordUpdateFailed('BookingGroup', groupId, error.message || 'Unknown error');
    throw error;
  }
}

export async function rejectBookingGroup(groupId: string): Promise<void> {
  const authToken = await getAuthToken();
  const functionUrl = getFunctionUrl('rejectBookingGroup');

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ groupId }),
  });

  if (!response.ok) {
    let errorData: any = { error: `Failed to reject group: ${response.statusText}` };
    try {
      errorData = await response.json();
    } catch {
      // ignore
    }
    const translatedError = translateApiError(errorData);
    const error = new Error(translatedError);
    (error as any).code = errorData.code || errorData.errorCode;
    throw error;
  }
}

// Add a record to a collection
export async function addRecord(collection: string, record: any) {
  try {
    const db = getDatabase();
    const newRef = push(ref(db, collection));
    await set(newRef, record);
    const recordId = newRef.key;
    await auditLog.recordCreated(collection, recordId || 'unknown', record);
    return recordId;
  } catch (error) {
    console.error('Error adding record:', error);
    await auditLog.recordCreationFailed(collection, error.message || 'Unknown error', record);
    throw error;
  }
}

// Check if a station is already booked for a given date range
export async function isStationBooked(stationId: string, fromDate: string, toDate: string): Promise<boolean> {
  try {
    const db = getDatabase();
    const bookingsSnap = await get(ref(db, 'Booking'));
    const bookings = bookingsSnap.val() || {};
    const from = new Date(fromDate);
    const to = new Date(toDate);
    let isBooked = false;
    
    for (const booking of Object.values(bookings)) {
      const bookingData = booking as any;
      if (String(bookingData.stationId) === String(stationId)) {
        const bFrom = new Date(bookingData.fromDate);
        const bTo = new Date(bookingData.toDate);
        if (isOverlap(from, to, bFrom, bTo)) {
          isBooked = true;
          break;
        }
      }
    }
    
    await auditLog.stationBookingCheck(stationId, fromDate, toDate, isBooked);
    return isBooked;
  } catch (error) {
    console.error('Error checking station booking:', error);
    await auditLog.stationBookingCheck(stationId, fromDate, toDate, false);
    return false;
  }
}

// Get bookings for a specific user (approval + keypadCode resolved from BookingGroup)
export async function getUserBookings(userEmail: string): Promise<Booking[]> {
  try {
    const [allBookings, groups] = await Promise.all([
      getAllBookings(),
      getAllBookingGroups(),
    ]);
    const userBookings = allBookings.filter((booking) => booking.userEmail === userEmail);
    return enrichBookingsWithGroups(userBookings, groups);
  } catch (error) {
    console.error('Error getting user bookings:', error);
    return [];
  }
}

// Subscribe to bookings changes
export function subscribeToBookings(callback: (bookings: any) => void) {
  const db = getDatabase();
  const bookingsRef = ref(db, 'Booking');
  return onValue(bookingsRef, (snapshot) => {
    callback(snapshot.val() || {});
  });
}

// Create a lock code by calling the Firebase HTTP function
export async function createLockCode(
  fromDate: string,
  toDate: string,
  userEmail: string,
  skipPendingRequest?: boolean,
  allowedWeekDays?: number,
  groupId?: string
): Promise<string> {
  try {
    // Convert dates to UTC ISO format before sending to Firebase function
    // Parse the dates (handles both with and without timezone info)
    const fromDateObj = new Date(fromDate);
    const toDateObj = new Date(toDate);
    
    // Validate dates
    if (isNaN(fromDateObj.getTime())) {
      throw new Error(`Invalid fromDate format: ${fromDate}`);
    }
    if (isNaN(toDateObj.getTime())) {
      throw new Error(`Invalid toDate format: ${toDate}`);
    }
    
    // Convert to UTC ISO strings
    const fromDateUTC = fromDateObj.toISOString();
    const toDateUTC = toDateObj.toISOString();
    
    // Get authentication token
    const authToken = await getAuthToken();
    
    // Build function URL
    const functionUrl = getFunctionUrl('createLockCode');
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        fromDate: fromDateUTC,
        toDate: toDateUTC,
        email: userEmail,
        skipPendingRequest: skipPendingRequest || false,
        ...(allowedWeekDays != null ? { allowedWeekDays } : {}),
        ...(groupId ? { groupId } : {}),
      }),
    });
    
    if (!response.ok) {
      // Try to read the error message and code from response body
      let errorData: any = { error: `Failed to create lock code: ${response.statusText}` };
      try {
        errorData = await response.json();
      } catch (e) {
        // If response is not JSON, use statusText
      }
      // Preserve error code in the Error object
      const translatedError = translateApiError(errorData);
      const error = new Error(translatedError);
      (error as any).code = errorData.code || errorData.errorCode;
      throw error;
    }
    
    const data = await response.json();
    
    if (data.code) {
      return data.code;
    } else {
      const translatedError = translateApiError(data);
      const error = new Error(translatedError);
      (error as any).code = data.code || data.errorCode;
      throw error;
    }
  } catch (error) {
    console.error('Error creating lock code:', error);
    // If error already has a code, translate it; otherwise use UNKNOWN_ERROR
    const errorCode = (error as any)?.code;
    const translatedError = translateApiError({ code: errorCode });
    const finalError = new Error(translatedError);
    (finalError as any).code = errorCode || 'UNKNOWN_ERROR';
    throw finalError;
  }
}

// Create a booking using the backend transaction function
export async function createBooking(
  stationId: string,
  fromDate: string,
  toDate: string,
  phoneNumber: string,
  groupId?: string
): Promise<{ bookingId: string; groupId: string; booking: Booking }> {
  try {
    // Get authentication token
    const authToken = await getAuthToken();
    
    // Build function URL
    const functionUrl = getFunctionUrl('createBooking');
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        stationId,
        fromDate,
        toDate,
        phoneNumber,
        ...(groupId ? { groupId } : {}),
      }),
    });
    
    if (!response.ok) {
      // Try to read the error message and code from response body
      let errorData: any = { error: `Failed to create booking: ${response.statusText}` };
      try {
        errorData = await response.json();
      } catch (e) {
        // If response is not JSON, use statusText
      }
      // Preserve error code in the Error object
      const translatedError = translateApiError(errorData);
      const error = new Error(translatedError);
      (error as any).code = errorData.code || errorData.errorCode;
      throw error;
    }
    
    const data = await response.json();
    
    if (data.success && data.bookingId && data.booking) {
      // Log successful booking creation
      await auditLog.bookingCreated(data.bookingId, data.booking);
      return {
        bookingId: data.bookingId,
        groupId: data.groupId,
        booking: data.booking
      };
    } else {
      const translatedError = translateApiError(data);
      const error = new Error(translatedError);
      (error as any).code = data.code || data.errorCode;
      throw error;
    }
  } catch (error: any) {
    console.error('Error creating booking:', error);
    // Log failed booking creation (keep original English for logging)
    const originalMessage = error instanceof Error ? error.message : String(error);
    await auditLog.bookingCreationFailed(originalMessage || 'Unknown error', {
      stationId,
      fromDate,
      toDate
    });
    // If error already has a code, translate it; otherwise use UNKNOWN_ERROR
    const errorCode = error?.code;
    const translatedError = translateApiError({ code: errorCode });
    const finalError = new Error(translatedError);
    (finalError as any).code = errorCode || 'UNKNOWN_ERROR';
    throw finalError;
  }
}